package diana.soleil.design.iraniancanadienbazzar.core.common

/**
 * Product analytics abstraction. Lives in `:core:common` so feature modules can emit events without
 * depending on `:core:firebase` (golden rule #5); the composition root binds the Firebase-backed
 * implementation when Firebase is initialized, else the no-op. Event names/params come from
 * [AnalyticsEvents]/[AnalyticsParams] — add new constants there (and to the taxonomy table in
 * `docs/RELEASE.md`) rather than inlining strings.
 */
interface AnalyticsTracker {
    fun track(event: String, params: Map<String, Any> = emptyMap())

    /** Associates events with the signed-in account (null on sign-out). */
    fun setUserId(uid: String?)
}

object NoOpAnalyticsTracker : AnalyticsTracker {
    override fun track(event: String, params: Map<String, Any>) = Unit
    override fun setUserId(uid: String?) = Unit
}

/**
 * Crash reporting abstraction (Crashlytics on Android/iOS; the web has no Crashlytics SDK, so its
 * binding is a no-op). Use [recordException] for caught-but-unexpected failures worth triaging.
 */
interface CrashReporter {
    fun recordException(throwable: Throwable)
    fun log(message: String)
    fun setUserId(uid: String?)
}

object NoOpCrashReporter : CrashReporter {
    override fun recordException(throwable: Throwable) = Unit
    override fun log(message: String) = Unit
    override fun setUserId(uid: String?) = Unit
}

/**
 * Process-global [CrashReporter] handle for layers below the DI graph (the repository error mapper
 * records non-fatals without every repository taking a constructor dependency). The composition
 * root assigns the platform reporter when Firebase telemetry binds; defaults to no-op.
 */
object CrashReporting {
    var reporter: CrashReporter = NoOpCrashReporter
}

/** Event names — mirror of the taxonomy table in `docs/RELEASE.md` (KPIs in `docs/MARKETING.md`). */
object AnalyticsEvents {
    const val BUSINESS_VIEW = "business_view"
    const val BUSINESS_CALL = "business_call"
    const val BUSINESS_DIRECTIONS = "business_directions"
    const val BUSINESS_WEBSITE = "business_website"
    const val BUSINESS_SAVE = "business_save"
    const val BUSINESS_FOLLOW = "business_follow"
    const val SEARCH_RESULT_CLICK = "search_result_click"
    /** A debounced, non-blank query produced a result list (fires once per settled query). */
    const val SEARCH_PERFORMED = "search_performed"
    const val EVENT_VIEW = "event_view"
    const val MAP_OPEN = "map_open"
    const val ADD_BUSINESS_START = "add_business_start"
    const val ADD_BUSINESS_COMPLETE = "add_business_complete"
    const val PLAN_INTEREST = "plan_interest"
    const val REVIEW_SUBMIT = "review_submit"
    const val POST_CREATE = "post_create"
    const val ORDER_PLACED = "order_placed"
    const val TICKET_CLAIMED = "ticket_claimed"
    const val MARKETPLACE_ITEM_VIEW = "marketplace_item_view"
    const val EVENT_OUTLINK = "event_outlink"
    const val ONBOARDING_START = "onboarding_start"
    const val ONBOARDING_LANGUAGE = "onboarding_language"
    const val ONBOARDING_COUNTRY = "onboarding_country"
    const val ONBOARDING_CITY = "onboarding_city"
    const val ONBOARDING_COMPLETE = "onboarding_complete"
}

/** Shared parameter keys. */
object AnalyticsParams {
    const val BUSINESS_ID = "business_id"
    const val CATEGORY_ID = "category_id"
    const val METRO_ID = "metro_id"
    const val COUNTRY = "country"
    const val QUERY_LENGTH = "query_length"
    const val RESULT_COUNT = "result_count"
    const val SAVED = "saved"
    const val FOLLOWING = "following"
    const val PLAN = "plan"
    const val ITEM_ID = "item_id"
    const val EVENT_ID = "event_id"
    const val LANGUAGE = "language"
    const val SKIPPED = "skipped"
    /** Whole seconds the user spent in the onboarding wizard (start → complete). */
    const val DURATION_S = "duration_s"
}
